export interface Vendors<T> {
  accounts: {
    nodes: T[];
    totalCount: number | undefined;
  };
}

export interface NestedResponse {
  default_payment_term_id: string;
  id: string;
  first_name: string;
  last_name: string;
  business_name?: string;
  business_type?: string;
  charges_enabled?: boolean;
  details_submitted?: boolean;
  treasury_account_id: string;
  card_id?: string;
  email?: string;
  phone?: string;
  limit?: number;
  offset?: number;
  status?: 'ACTIVE' | 'INACTIVE' | 'INVITED' | string;
  payoutsByAccountId: {
    groupedAggregates: [
      {
        sum: {
          amount: string;
          payout_amount: string;
          payout_term_amount: string;
        };
      }
    ];
  };
}

export interface VendorsListResponse {
  limit?: number;
  offset?: number;
  status?: string;
}

export const InitialState = {
  first: 20,
  offset: 0,
  filter: { status: { equalTo: 'ACTIVE' } },
};

/**
 * Not-onboarded vendors (CoupaSupplier-anchored).
 * Matches the Slice 1 `POST /coupa/not-onboarded-vendors` response contract.
 */
export interface NotOnboardedVendor {
  identityKey: string;
  coupaSupplierId: string;
  name: string | null;
  code: string;
  email: string | null;
  primaryContactName: string | null;
  phone: string | null;
  street1: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  taxId: string | null;
  status: string | null;
  coupaRecordCount: number;
  canInvite: boolean;
  inviteStatus?: 'invited' | 'not_invited';
  invitedAt?: string | null;
}

/**
 * Managed onboarding: `POST /account/onboard-managed` (super-admin/admin).
 * Creates a check/digital_check vendor Account + links its CoupaSupplier.
 */
export interface OnboardManagedRequest {
  account: {
    company: { name: string; tax_id: string };
    email: string;
    default_pay_mode: 'check' | 'digital_check';
    business_type?: 'company';
    language?: string;
    /** super-admin override: mint a tagged placeholder when no EIN is on file */
    allowPlaceholderEin?: boolean;
    check_recipient_details?: {
      name: string;
      address: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
    };
  };
  link: {
    coupaSupplierId: string;
    force?: boolean;
    checkProcessingFeeAmountOverride?: number;
  };
}

export interface OnboardManagedResponse {
  account: { id: string; stripe_id?: string; [key: string]: unknown };
  link: { linked?: boolean; [key: string]: unknown };
}

export interface InviteVendorsRequest {
  identityKeys: string[];
}

export interface InviteVendorItemResult {
  identityKey: string;
  status: 'invited' | 'skipped';
  reason?: 'not_found' | 'no_contact';
  invitedAt?: string;
}

export interface InviteVendorsResponse {
  results: InviteVendorItemResult[];
}

export interface NotOnboardedVendorsResponse {
  vendors: NotOnboardedVendor[];
  totalCount: number;
}

export interface NotOnboardedVendorsParams {
  first: number;
  offset: number;
  search?: string;
}

export const NotOnboardedInitialState: NotOnboardedVendorsParams = {
  first: 20,
  offset: 0,
  search: '',
};
